'use client';

import { useEffect, useRef, useState } from 'react';
import type { RealtimeClient, RealtimeChannel } from '@supabase/realtime-js';
import type {
  Product,
  PriceInfoType,
  ImageType,
  ExternalLinks,
  Offer,
  OfferAvailability,
} from 'shared/src/types/product.type';
import { rankOffers } from 'shared/src/utils/offers/rank-offers';
import { isOfferRowFresh } from 'shared/src/utils/offers/read-window';
import { promotedImageSrc } from 'shared/src/server/products/images/image-revision';
import { offerGroupKey } from 'shared/src/server/products/offers/offer-key';

/**
 * Per-product fields that may be refreshed by the background Amazon/Bing pipeline.
 * Anything else (title, benefits, etc.) stays static post-render.
 */
export type LiveProductFields = {
  price?: PriceInfoType;
  images?: ImageType[];
  externalLinks?: ExternalLinks;
  ASIN?: string;
  lastUpdated?: { seconds?: number; _seconds?: number } | string | null;
  review?: string;
  reviewSource?: 'ai-estimate' | 'reviews';
  /**
   * This product's reviews in the updated row's customerReviewsAggregate, and
   * how many source types they come from: the proof a promoted summary belongs
   * to THIS page (groundedReviewText). Absent when the realtime row did not
   * carry the aggregate; the SSR counts then stand.
   */
  reviewQuoteCount?: number;
  reviewSourceCount?: number;
  /**
   * The product's offers for THIS market, re-ranked after a `product_offers`
   * change. Arrives on its own channel and its own clock: offers are written by
   * three writers none of which touch `categories.products`, so they can never
   * ride in on the categories UPDATE above.
   */
  offers?: Offer[];
  /**
   * What the card identity guard refused in what the snapshot brought (the
   * link's listing, photos): judged here by the guard loaded on demand
   * (judgeLiveIdentity), applied by the card's presentation
   * (present-live-product.ts) together with the server's own stamps.
   */
  refusedListings?: string[];
  refusedImages?: string[];
};

type ProductBaseline = {
  title: string;
  seconds: number;
  /**
   * The key this product's offers are filed under ('asin:B0…' / 'name:…'),
   * from `offerGroupKey`. Required for the offers channel: a `product_offers`
   * row identifies itself by group key, not by product title, so without this
   * an incoming row cannot be attributed to anything on the page.
   */
  offerGroupKey?: string;
  /** The SSR offer set, so an incoming single-row change is merged INTO the
   *  list the reader is looking at rather than replacing it with one row. */
  offers?: Offer[];
  /** The brand, so the client re-rank runs the same pick ladder as SSR. */
  brandName?: string;
  /** Last promoted image revision from SSR, so a live bump is visible. */
  imageRevision?: number;
  /** The page's own product (a product search's first product): judged in brandOnly mode. */
  searchedProduct?: boolean;
};

export function shouldAttachOffersRealtime(
  offersRegionCode: string | null | undefined,
): offersRegionCode is string {
  return Boolean(offersRegionCode);
}

export function liveFieldsFromCategoryProduct(
  product: Product,
  previous?: LiveProductFields,
  aggregate?: unknown,
): LiveProductFields {
  return {
    price: product.price,
    images: product.images,
    externalLinks: product.externalLinks,
    ASIN: product.ASIN,
    lastUpdated: product.lastUpdated as LiveProductFields['lastUpdated'],
    review: product.review,
    reviewSource: product.reviewSource,
    ...pageReviewCounts(aggregate, product.title),
    ...(previous?.offers ? { offers: previous.offers } : {}),
  };
}

/**
 * `{ reviewQuoteCount, reviewSourceCount }` for one product from a categories
 * row's `customer_reviews_aggregate`, or `{}` when the row carried none.
 */
export function pageReviewCounts(
  aggregate: unknown,
  title: string | undefined,
): Pick<LiveProductFields, 'reviewQuoteCount' | 'reviewSourceCount'> {
  if (!title || !aggregate || typeof aggregate !== 'object') return {};
  const byProduct = (aggregate as { byProduct?: unknown }).byProduct;
  if (!byProduct || typeof byProduct !== 'object') return {};
  const quotes = (byProduct as Record<string, unknown>)[title];
  if (!Array.isArray(quotes)) return { reviewQuoteCount: 0, reviewSourceCount: 0 };
  const sources = new Set(
    quotes.map((quote) => (quote as { sourceType?: unknown } | null)?.sourceType),
  );
  return { reviewQuoteCount: quotes.length, reviewSourceCount: sources.size };
}

// ─── The card identity guard, on demand ─────────────────────────────────────
//
// The guard and its brand lists (about 36 KB gzip) never ship with a page's
// cards: the server presented every card with them and stamped what they
// refused. A realtime snapshot can bring a listing or a photo the server never
// judged, so the guard is loaded here, as a chunk of its own, the first time
// such a snapshot arrives (never on a page nobody updates).

type LiveRefusals = Pick<LiveProductFields, 'refusedListings' | 'refusedImages'>;

/** The market of a docPath locale ("en-UK" → "UK"), as the server presents a page. */
export function regionOfLocale(locale: string): string | undefined {
  return locale.split('-')[1]?.toUpperCase() || undefined;
}

/**
 * The guard's refusals for each product (by title). An empty map when the
 * guard cannot be loaded: the server's stamps then still apply, and what only
 * the snapshot brings is shown as the server's pipeline would show it without
 * the guard.
 */
export async function judgeLiveIdentity(
  products: Product[],
  regionCode: string | undefined,
  searched: ReadonlySet<string>,
): Promise<Map<string, LiveRefusals>> {
  const out = new Map<string, LiveRefusals>();
  if (!regionCode || products.length === 0) return out;
  try {
    const { judgeLiveProduct } = await import('./judge-live-identity');
    for (const product of products) {
      if (product?.title) {
        out.set(product.title, judgeLiveProduct(product, regionCode, searched.has(product.title)));
      }
    }
  } catch (err) {
    console.warn('[useLiveProducts] card identity guard unavailable; the server stamps still apply', err);
  }
  return out;
}

/** The snapshot's fields with the guard's refusals, added to those already held. */
export function withRefusals(
  fields: LiveProductFields,
  previous: LiveProductFields | undefined,
  refusals: LiveRefusals | undefined,
): LiveProductFields {
  const merge = (a?: string[], b?: string[]) =>
    a?.length || b?.length ? [...new Set([...(a ?? []), ...(b ?? [])])] : undefined;
  const refusedListings = merge(previous?.refusedListings, refusals?.refusedListings);
  const refusedImages = merge(previous?.refusedImages, refusals?.refusedImages);
  return {
    ...fields,
    ...(refusedListings ? { refusedListings } : {}),
    ...(refusedImages ? { refusedImages } : {}),
  };
}

const tsSeconds = (t: LiveProductFields['lastUpdated'] | undefined | null): number => {
  if (!t) return 0;
  if (typeof t === 'string') return Math.floor(new Date(t).getTime() / 1000);
  return (t as any).seconds ?? (t as any)._seconds ?? 0;
};

// ─── product_offers → Offer ──────────────────────────────────────────────────
//
// A realtime payload is the RAW ROW: snake_case columns, integer cents. The
// server-side converter (offers-store.ts `offerRowToOffer`) speaks camelCase and
// lives in a module that imports postgres.js, so it can neither be reused nor
// bundled here. This is the client's own boundary conversion, and it must keep
// the SAME conventions the server one does, or a live arrival will render
// differently from the same offer after a reload:
//   - cents ÷ 100, fixed exponent 2 for every currency (JPY included);
//   - no price without a currency (a bare number is not a price);
//   - `verifiedAt` and `observedAt` as ISO strings, observedAt falling back
//     to the success clock when the writer set none;
//   - `tracking_url_enc` copied verbatim as `trackingToken`, never parsed: it
//     is ciphertext, and a Realtime row carrying it is harmless without the
//     key the /go hop alone holds (spec 5.3);
//   - `kind` derived exactly as the SQL does ('info' = brand-site, no price);
//   - `program_id` and `feed_item_id` dropped: nothing on a page needs them.
//
// Two facts a raw row cannot carry are inherited from the SSR row for the same
// merchant, when there is one: the 60-day typical price (computed by the read
// path's LATERAL, not stored on the row) and the program's attribution flag
// (a merchant_programs lookup). A live price change does not move either.

/** `product_offers` as Realtime hands it back (the columns this converter reads). */
export type OfferRowPayload = {
  id?: string | null;
  offer_group_key?: string | null;
  region_code?: string | null;
  merchant_domain?: string | null;
  merchant_name?: string | null;
  merchant_kind?: string | null;
  url?: string | null;
  tracking_url_enc?: string | null;
  price_cents?: number | string | null;
  currency?: string | null;
  original_price_cents?: number | string | null;
  price_kind?: string | null;
  availability?: string | null;
  source?: string | null;
  confidence?: number | string | null;
  evidence?: unknown;
  is_dead?: boolean | null;
  last_verified_at?: string | null;
  observed_at?: string | null;
};

const centsToMajor = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n / 100 : null;
};

const isoOrUndefined = (raw: string | null | undefined): string | undefined => {
  if (!raw) return undefined;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? new Date(ms).toISOString() : undefined;
};

const MERCHANT_KINDS: ReadonlySet<string> = new Set(['retailer', 'brand', 'marketplace']);
const PRICE_KINDS: ReadonlySet<string> = new Set(['sale', 'regular', 'msrp', 'member', 'hidden']);

/** `evidence` as a parsed object, whether Realtime delivered it parsed or as text. */
const evidenceOf = (raw: unknown): Record<string, unknown> => {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return {};
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
};

export function offerFromPayload(row: OfferRowPayload, previous?: Offer): Offer | null {
  const merchantDomain = row.merchant_domain?.trim().toLowerCase();
  const url = row.url?.trim();
  if (!merchantDomain || !url || !row.source) return null;

  const currency = row.currency ?? undefined;
  const value = centsToMajor(row.price_cents);
  const originalValue = centsToMajor(row.original_price_cents);
  const confidence = row.confidence === null || row.confidence === undefined
    ? undefined
    : Number(row.confidence);
  const verifiedAt = isoOrUndefined(row.last_verified_at);
  const observedAt = isoOrUndefined(row.observed_at) ?? verifiedAt;
  const evidence = evidenceOf(row.evidence);
  const dealRaw = evidence.deal;
  const deal =
    dealRaw && typeof dealRaw === 'object' && !Array.isArray(dealRaw)
      ? {
          ...(typeof (dealRaw as Record<string, unknown>).badge === 'string'
            ? { badge: (dealRaw as Record<string, unknown>).badge as string }
            : {}),
          ...(typeof (dealRaw as Record<string, unknown>).endsAt === 'string'
            ? { endsAt: (dealRaw as Record<string, unknown>).endsAt as string }
            : {}),
        }
      : undefined;
  const merchantKind =
    typeof row.merchant_kind === 'string' && MERCHANT_KINDS.has(row.merchant_kind)
      ? (row.merchant_kind as Offer['merchantKind'])
      : undefined;
  const priceKind =
    typeof row.price_kind === 'string' && PRICE_KINDS.has(row.price_kind)
      ? (row.price_kind as Offer['priceKind'])
      : undefined;
  const kind: Offer['kind'] = row.source === 'brand-site' && value === null ? 'info' : 'offer';

  return {
    ...(row.id ? { offerId: String(row.id) } : {}),
    merchantDomain,
    ...(row.merchant_name ? { merchantName: row.merchant_name } : {}),
    ...(merchantKind ? { merchantKind } : {}),
    url,
    ...(typeof row.tracking_url_enc === 'string' && row.tracking_url_enc
      ? { trackingToken: row.tracking_url_enc }
      : {}),
    ...(value !== null && currency ? { price: { value, currency } } : {}),
    ...(originalValue !== null && currency
      ? { originalPrice: { value: originalValue, currency } }
      : {}),
    ...(priceKind ? { priceKind } : {}),
    kind,
    ...(row.availability ? { availability: row.availability as OfferAvailability } : {}),
    source: row.source as Offer['source'],
    ...(confidence !== undefined && Number.isFinite(confidence) ? { confidence } : {}),
    ...(verifiedAt ? { verifiedAt } : {}),
    ...(observedAt ? { observedAt } : {}),
    ...(previous?.typicalPrice ? { typicalPrice: previous.typicalPrice } : {}),
    ...(typeof previous?.typicalPricePoints === 'number'
      ? { typicalPricePoints: previous.typicalPricePoints }
      : {}),
    ...(deal && (deal.badge !== undefined || deal.endsAt !== undefined) ? { deal } : {}),
    ...(typeof evidence.reason === 'string' && evidence.reason ? { infoReason: evidence.reason } : {}),
    ...(typeof evidence.why === 'string' && evidence.why ? { why: evidence.why } : {}),
    ...(previous?.attributionRequired ? { attributionRequired: true } : {}),
  };
}

/** Is this row still eligible to be shown? Mirrors the SQL read filter
 *  (`offer_row_is_fresh`): not dead, and verified inside the source's window,
 *  48 h for a feed row, 24 h for the Amazon row, 30 days for everything else. */
export function offerRowIsLive(row: OfferRowPayload, now: number = Date.now()): boolean {
  if (row.is_dead === true) return false;
  return isOfferRowFresh(row.source, row.last_verified_at, now);
}

// Realtime-only client for postgres_changes subscriptions.
//
// We import `@supabase/realtime-js` directly (not the umbrella `@supabase/supabase-js`)
// so the bundle carries ONLY the realtime transport — not auth-js/storage-js/
// postgrest-js/functions-js, which this hook never touches. The import is dynamic and
// only fires from inside the effect below (post-hydration, client-only), so none of the
// Supabase code lands on the product page's initial/critical JS path.
let _clientPromise: Promise<RealtimeClient | null> | null = null;
function getRealtimeClient(): Promise<RealtimeClient | null> {
  if (!_clientPromise) {
    _clientPromise = import('@supabase/realtime-js')
      .then(({ RealtimeClient }) => {
        const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
        const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
        if (!url || !anonKey) {
          console.warn(
            '[useLiveProducts] NEXT_PUBLIC_SUPABASE_URL / NEXT_PUBLIC_SUPABASE_ANON_KEY missing — live product updates disabled',
          );
          return null;
        }
        // Mirror what @supabase/supabase-js does internally: pass the anon key both as the
        // connection `apikey` param and as the access token (no auth session → the anon JWT
        // is what postgres_changes RLS evaluates).
        return new RealtimeClient(`${url}/realtime/v1`, {
          params: { apikey: anonKey },
          accessToken: async () => anonKey,
        });
      })
      .catch((err) => {
        // Don't permanently cache a rejected promise — allow a later retry if the
        // dynamic import / constructor fails transiently.
        _clientPromise = null;
        console.warn('[useLiveProducts] Failed to init Realtime client', err);
        return null;
      });
  }
  return _clientPromise;
}

/**
 * Subscribes to a single categories row in Supabase and exposes per-product
 * live overrides for fields that the background refresh pipeline rewrites.
 *
 * - docPath format: "id:locale" (e.g. "gaming-laptops:en-US")
 * - Lazy: no subscription is opened when docPath is empty; the Supabase client
 *   module is dynamically imported only when we actually attach.
 * - Visibility-aware: detaches while the tab is hidden, re-attaches on focus.
 * - Per-product freshness guard: only accepts fields strictly newer than baseline.
 *
 * OFFERS (second listener, same channel — one websocket either way). Passing
 * `offersRegionCode` plus baseline entries carrying `offerGroupKey` adds a
 * `product_offers` subscription for that MARKET, so the offers a human visit
 * just paid for appear in the panel seconds later instead of on the next
 * navigation. Filtered on `region_code` because Realtime supports single-column
 * filters only; the group key is matched client-side against the page's own
 * products, so a row for a product nobody here is looking at is dropped in one
 * map lookup.
 *
 * 🚨 THE 30s BUY-INTENT PIN IS INHERITED, NOT REIMPLEMENTED. `offers` rides in
 * the same per-title `LiveProductFields` entry every other live field does, and
 * LiveProductsProvider masks a frozen product by deleting its whole entry from
 * the map — so a product the reader is mid-checkout on falls back to its SSR
 * offer list and the panel cannot re-order under their cursor. The working set
 * below keeps accumulating while frozen; when the pin lapses the reader gets the
 * current truth in one step. A second freeze check here would be a second thing
 * to get out of sync, which is precisely how a list ends up shuffling anyway.
 */
export function useLiveProducts(
  docPath: string | undefined,
  initialBaseline: ProductBaseline[] | undefined,
  offersRegionCode?: string,
): Map<string, LiveProductFields> {
  const [overrides, setOverrides] = useState<Map<string, LiveProductFields>>(() => new Map());
  const baselineRef = useRef<Map<string, number>>(new Map());
  const imageRevisionRef = useRef<Map<string, number>>(new Map());
  /** offer_group_key → product title, for attributing an incoming offer row. */
  const offerGroupToTitleRef = useRef<Map<string, string>>(new Map());
  /** title → merchant_domain → Offer. The working set a single-row change is
   *  merged into, seeded from SSR so a live arrival never truncates the list. */
  const offerSetsRef = useRef<Map<string, Map<string, Offer>>>(new Map());
  /** title → brandName, for the pick ladder's brand-store rung. */
  const brandByTitleRef = useRef<Map<string, string>>(new Map());
  /** Titles of the page's own product (judged in brandOnly mode). */
  const searchedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const baseline = new Map<string, number>();
    const revisions = new Map<string, number>();
    const groups = new Map<string, string>();
    const sets = new Map<string, Map<string, Offer>>();
    const brands = new Map<string, string>();
    const searched = new Set<string>();
    for (const p of initialBaseline ?? []) {
      if (!p?.title) continue;
      if (p.searchedProduct) searched.add(p.title);
      baseline.set(p.title, p.seconds);
      if (typeof p.imageRevision === 'number') revisions.set(p.title, p.imageRevision);
      if (p.offerGroupKey) groups.set(p.offerGroupKey, p.title);
      if (p.brandName) brands.set(p.title, p.brandName);
      const seeded = new Map<string, Offer>();
      for (const offer of p.offers ?? []) {
        if (offer?.merchantDomain) seeded.set(offer.merchantDomain, offer);
      }
      sets.set(p.title, seeded);
    }
    baselineRef.current = baseline;
    imageRevisionRef.current = revisions;
    offerGroupToTitleRef.current = groups;
    offerSetsRef.current = sets;
    brandByTitleRef.current = brands;
    searchedRef.current = searched;
  }, [initialBaseline]);

  useEffect(() => {
    if (!docPath) return;

    // docPath format: "id:locale"
    const [catId, locale] = docPath.split(':');
    if (!catId || !locale) return;
    const regionCode = regionOfLocale(locale);

    // Guards the async client import: if the effect cleans up (unmount / docPath change)
    // before the dynamic import resolves, we must not create a channel afterwards.
    let cancelled = false;
    let client: RealtimeClient | null = null;
    let channel: RealtimeChannel | null = null;

    const attach = async () => {
      if (cancelled || channel) return;
      try {
        client = await getRealtimeClient();
        if (cancelled || channel || !client) return; // re-check: cleanup may have run during the await

        let pending: RealtimeChannel = client
          .channel(`cat-${catId}-${locale}`)
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'categories',
              // Realtime supports single-column filters only — compound
              // "id=eq.X&locale=eq.Y" silently matches nothing. Filter on id
              // server-side and guard locale client-side.
              filter: `id=eq.${catId}`,
            },
            (payload) => {
              // postgres_changes sends no initial snapshot (unlike Firestore
              // onSnapshot) — every event is a genuine row update. The
              // per-product freshness baseline below already prevents
              // re-applying SSR-equivalent data.
              if ((payload.new as any)?.locale !== locale) return;

              const products: Product[] | undefined = (payload.new as any)?.products;
              if (!products?.length) return;
              const aggregate = (payload.new as any)?.customer_reviews_aggregate;

              // The products this snapshot refreshes are judged by the card
              // identity guard first (loaded on demand), then applied.
              const fresh = products.filter(
                (p) => !!p?.title && tsSeconds(p.lastUpdated as any) > (baselineRef.current.get(p.title) ?? 0),
              );
              const apply = (refusals: Map<string, LiveRefusals>) => {
                if (cancelled) return;
                setOverrides((prev) => {
                  let changed = false;
                  const next = new Map(prev);
                  for (const p of products) {
                    if (!p?.title) continue;
                    const incomingOfferGroupKey = offerGroupKey(p);
                    if (incomingOfferGroupKey) {
                      // Keep the original name key and add the ASIN key when
                      // enrichment discovers identity after first paint.
                      offerGroupToTitleRef.current.set(
                        incomingOfferGroupKey,
                        p.title,
                      );
                    }
                    const incomingSeconds = tsSeconds(p.lastUpdated as any);
                    const baselineSeconds = baselineRef.current.get(p.title) ?? 0;
                    if (!incomingSeconds || incomingSeconds <= baselineSeconds) {
                      // The review promotion rides on its own clock: it never
                      // bumps lastUpdated (the price observation date), so a
                      // grounded summary is applied on its own.
                      const previous = prev.get(p.title);
                      if (
                        p.reviewSource === 'reviews' &&
                        typeof p.review === 'string' &&
                        p.review.trim() &&
                        (previous?.review !== p.review || previous?.reviewSource !== 'reviews')
                      ) {
                        next.set(p.title, {
                          ...(previous ?? {}),
                          review: p.review,
                          reviewSource: 'reviews',
                          ...pageReviewCounts(aggregate, p.title),
                        });
                        changed = true;
                      }
                      continue;
                    }

                    next.set(
                      p.title,
                      withRefusals(
                        liveFieldsFromCategoryProduct(p, prev.get(p.title), aggregate),
                        prev.get(p.title),
                        refusals.get(p.title),
                      ),
                    );
                    baselineRef.current.set(p.title, incomingSeconds);
                    changed = true;
                  }
                  return changed ? next : prev;
                });
              };
              if (fresh.length === 0) apply(new Map());
              else void judgeLiveIdentity(fresh, regionCode, searchedRef.current).then(apply);
            },
          );

        pending = pending.on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'products',
            filter: `locale=eq.${locale}`,
          },
          (payload) => {
            const row = (payload.new ?? {}) as {
              display_name?: string;
              image_url?: string | null;
              image_status?: string | null;
              image_revision?: number | null;
              asin?: string | null;
            };
            const title = row.display_name;
            if (!title || !baselineRef.current.has(title)) return;
            if (row.asin) {
              offerGroupToTitleRef.current.set(`asin:${row.asin}`, title);
            }
            const previous = imageRevisionRef.current.get(title) ?? 0;
            const src = promotedImageSrc({
              imageUrl: row.image_url,
              status: row.image_status,
              revision: row.image_revision,
              previousRevision: previous,
            });
            if (!src) return;
            const revision = Number(row.image_revision ?? 0) || 0;
            imageRevisionRef.current.set(title, revision);
            const image = { src, alt: title, type: 'other' as const };
            // The promoted photo is judged like any photo a snapshot brings.
            const probe = {
              title,
              brandName: brandByTitleRef.current.get(title) ?? '',
              images: [image],
            } as unknown as Product;
            void judgeLiveIdentity([probe], regionCode, searchedRef.current).then((refusals) => {
              if (cancelled) return;
              setOverrides((prev) => {
                const next = new Map(prev);
                const previous = prev.get(title);
                next.set(title, withRefusals({ ...(previous ?? {}), images: [image] }, previous, refusals.get(title)));
                return next;
              });
            });
          },
        );

        // Second listener, same channel: `product_offers` for this market.
        // Attach even when the initial streaming baseline is empty: products
        // and ASIN keys arrive later through SSE/category updates, while this
        // effect intentionally remains attached for the lifetime of docPath.
        if (shouldAttachOffersRealtime(offersRegionCode)) {
          pending = pending.on(
            'postgres_changes',
            {
              // INSERT, UPDATE and DELETE alike: a merchant going out of stock
              // or being marked dead is as much news as a new price.
              event: '*',
              schema: 'public',
              table: 'product_offers',
              filter: `region_code=eq.${offersRegionCode}`,
            },
            (payload) => {
              const row = ((payload.new ?? payload.old) ?? {}) as OfferRowPayload;
              const groupKey = row.offer_group_key;
              const merchantDomain = row.merchant_domain?.trim().toLowerCase();
              if (!groupKey || !merchantDomain) return;

              const title = offerGroupToTitleRef.current.get(groupKey);
              if (!title) return; // a product nobody on this page is looking at

              const set = offerSetsRef.current.get(title) ?? new Map<string, Offer>();
              const removed = payload.eventType === 'DELETE' || !offerRowIsLive(row);
              // The SSR row for the same merchant lends the facts a raw row
              // cannot carry (typical price, attribution); see the converter.
              const offer = removed ? null : offerFromPayload(row, set.get(merchantDomain));

              if (removed) {
                if (!set.delete(merchantDomain)) return; // nothing to retract
              } else if (offer) {
                set.set(merchantDomain, offer);
              } else {
                return; // unusable row (no url / no source) — ignore, don't clear
              }
              offerSetsRef.current.set(title, set);

              // rankOffers is the ONE ranking truth — the same pure function the
              // server ran for the SSR list. Re-running it here is what keeps a
              // live arrival from landing in a different order than a reload.
              const ranked = rankOffers([...set.values()], offersRegionCode, {
                brand: brandByTitleRef.current.get(title) ?? null,
              });

              setOverrides((prev) => {
                const next = new Map(prev);
                next.set(title, { ...(prev.get(title) ?? {}), offers: ranked });
                return next;
              });
            },
          );
        }

        channel = pending.subscribe((status) => {
          if (status === 'CHANNEL_ERROR') {
            console.warn('[useLiveProducts] Supabase channel error for', docPath);
          }
        });
      } catch (err) {
        console.warn('[useLiveProducts] Failed to attach Realtime channel', err);
      }
    };

    const detach = () => {
      if (channel && client) {
        client.removeChannel(channel);
        channel = null;
      }
    };

    const onVisibilityChange = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState === 'hidden') {
        detach();
      } else {
        void attach();
      }
    };

    if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
      void attach();
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    return () => {
      cancelled = true;
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
      detach();
    };
  }, [docPath, offersRegionCode]);

  return overrides;
}
